'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { 
  ChevronDown, Copy, Check, Plus, 
  ShoppingCart, LayoutDashboard, Settings, LogOut,
  ShieldAlert, MessageCircle 
} from 'lucide-react';

import { useAuthContext } from '@/components/AuthProvider';
import { useModal } from '@/components/ModalProvider';
import { useLanguage } from '@/components/LanguageProvider'; 
import InboxDropdown from './InboxDropdown'; 

const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API;

interface UserNavProps {
  mode?: 'default' | 'hero' | 'account-menu';
  user?: any; 
  logout?: () => void;
  // Глобальный переключатель (скроет везде)
  showBalance?: boolean;
  // Точечные переключатели
  showOuterBalance?: boolean; // Внешняя кнопка баланса (рядом с аватаркой)
  showInnerBalance?: boolean; // Внутренний блок баланса (в выпадающем меню)
  
  onOpenDeposit?: () => void;
  alignDropdownToHeader?: boolean;
}

export default function UserNav({ 
  mode = 'default',
  user: propUser,
  logout: propLogout,
  showBalance = true,
  showOuterBalance = true,
  showInnerBalance = true,
  onOpenDeposit,
  alignDropdownToHeader = false
}: UserNavProps) {
  
  const authContext = useAuthContext();
  const { user: authUser, isAuthenticated, isLoading, logout: authLogout, openLoginModal } = authContext;
  
  const { openDeposit } = useModal();
  const { t } = useLanguage(); 

  const tm = t.auth?.menu || {};
  const tn = t.userNav || {};
  const txtLogin = tn.login || 'Войти';
  const txtBalance = tm.balance || tn.balance || 'Баланс';
  const txtBuy = tm.buyAccess || 'Купить проходку';
  const txtAdmin = tm.adminPanel || 'Админ-панель';
  const txtDash = tm.dashboard || tn.dashboard || 'Личный кабинет';
  const txtSettings = tm.settings || tn.settings || 'Настройки';
  const txtLogout = tm.logout || tn.logout || 'Выйти';

  const activeUser = propUser || authUser;
  const activeLogout = propLogout || authLogout;
  const handleOpenDeposit = () => {
    if (onOpenDeposit) onOpenDeposit();
    else if (openDeposit) openDeposit();
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadCount = useRef(0);
  const isFirstCheck = useRef(true); 
  const containerRef = useRef<HTMLDivElement>(null);

  const displayId = activeUser?.playerId || (activeUser?.id ? 500000000 + activeUser.id : 0);
  const formatCrystals = (amount: number | undefined) => (amount ?? 0).toLocaleString('ru-RU');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setIsInboxOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkUnreadNotifications = async () => {
    if (!activeUser?.oauthId) return;
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${activeUser.oauthId}/unread-count`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (!isFirstCheck.current && data.count > prevUnreadCount.current) {
          const audio = new Audio('/sounds/pop.mp3');
          audio.volume = 0.5;
          audio.play().catch(e => console.log("Браузер заблокировал автовоспроизведение звука", e));
        }
        isFirstCheck.current = false;
        prevUnreadCount.current = data.count;
        setUnreadCount(data.count);
      }
    } catch (e) {
      console.error("Ошибка проверки уведомлений:", e);
    }
  };

  useEffect(() => {
    if (mode === 'account-menu' || mode === 'hero') return;

    if (isAuthenticated && activeUser?.oauthId) {
      checkUnreadNotifications();
      const interval = setInterval(checkUnreadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, activeUser?.oauthId, mode]);

  const copyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayId) {
      navigator.clipboard.writeText(displayId.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleMarkAsReadSuccess = () => {
    setUnreadCount(0);
    prevUnreadCount.current = 0;
  };

  const toggleInbox = () => {
    setIsInboxOpen(!isInboxOpen);
    setIsDropdownOpen(false);
  };

  const toggleProfile = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsInboxOpen(false);
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse bg-white/10 ${
        mode === 'hero' ? 'w-14 h-14 rounded-lg' : mode === 'account-menu' ? 'w-8 h-8 rounded-full' : 'w-10 h-10 rounded-full'
      }`} />
    );
  }

  if (!isAuthenticated || !activeUser) {
    if (mode === 'account-menu') return null;

    return (
      <button
        type="button"
        onClick={openLoginModal}
        className={`bg-transparent border-none p-0 outline-none text-white hover:opacity-65 transition-opacity cursor-pointer select-none font-medium ${
          mode === 'hero' ? 'text-[22px] font-bold' : 'text-[18px]'
        }`}
      >
        {txtLogin}
      </button>
    ); 
  }

  return (
    <div className={`relative z-50 flex items-center ${mode === 'hero' ? 'gap-0' : 'gap-5 sm:gap-6'} ${alignDropdownToHeader ? 'h-full' : ''}`} ref={containerRef}>
      
      {/* КНОПКА УВЕДОМЛЕНИЙ */}
      {mode === 'default' && (
        <div className="relative flex items-center">
          <button 
            onClick={toggleInbox} 
            className="relative flex items-center justify-center text-white transition-opacity duration-200 hover:opacity-65 outline-none cursor-pointer"
          >
            <MessageCircle size={24} strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-[18px] h-[18px] bg-red-500 rounded-full border-[2px] border-[#111111] text-[9px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <div className={`absolute top-full right-0 mt-5 transition-all duration-200 z-50 ${isInboxOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
             <InboxDropdown 
               isOpen={isInboxOpen} 
               onClose={() => setIsInboxOpen(false)} 
               user={activeUser} 
               onMarkAsReadSuccess={handleMarkAsReadSuccess}
             />
          </div>
        </div>
      )}

      {/* ВНЕШНЯЯ КНОПКА БАЛАНСА (Показываем только если showBalance И showOuterBalance == true) */}
      {mode === 'default' && showBalance && showOuterBalance && (
        <button 
          onClick={handleOpenDeposit} 
          className="hidden sm:flex items-center gap-3 bg-[#151515] border border-[#252525] hover:border-purple-500/50 rounded-full pl-4 pr-1.5 py-1.5 transition-all group cursor-pointer outline-none"
        >
          <div className="flex flex-col items-start leading-none">
            <span className="text-[10px] text-zinc-500 font-bold uppercase">{txtBalance}</span>
            <span className="text-sm font-bold text-white">{formatCrystals(activeUser.crystals)} 💎</span>
          </div>
          <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-white shadow-lg shadow-purple-900/20 group-hover:scale-110 transition-transform">
            <Plus size={14} strokeWidth={3} />
          </div>
        </button>
      )}

      {/* КНОПКА ПРОФИЛЯ (Триггер выпадающего меню) */}
      <div className={`relative flex items-center ${alignDropdownToHeader ? 'h-full' : ''}`}>
        <div 
          onClick={toggleProfile}
          className={`flex items-center cursor-pointer transition-all select-none ${
            mode === 'hero' 
              ? 'p-0 hover:scale-105' 
              : 'gap-2 pl-2 pr-3 py-1.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 backdrop-blur-md'
          }`}
        >
          <Image 
            src={`${LOCAL_API_URL}/morda/${activeUser.nickname}`} 
            width={mode === 'hero' ? 56 : 32} 
            height={mode === 'hero' ? 56 : 32} 
            alt="Avatar" 
            className={`bg-black/20 relative z-10 transition-transform ${
              mode === 'hero' 
                ? 'w-14 h-14 rounded-lg object-cover shadow-none' 
                : 'w-8 h-8 rounded-full'
            }`} 
            unoptimized
          />
          {mode !== 'hero' && (
            <ChevronDown size={16} className={`text-white/60 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180 text-white' : ''}`} />
          )}
        </div>

        {/* ВЫПАДАЮЩЕЕ МЕНЮ */}
        <div className={`absolute top-full right-0 mt-3 w-[310px] transition-all duration-300 origin-top-right ${
          isDropdownOpen ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
        }`}>
          <div className="bg-[#18181f]/95 backdrop-blur-xl border border-white/10 rounded-3xl p-5 shadow-2xl">
            
            <div className="flex items-center gap-4 mb-4">
              <Image 
                src={`${LOCAL_API_URL}/morda/${activeUser.nickname}`} 
                alt="Avatar" 
                width={54} 
                height={54} 
                className="rounded-2xl bg-white/5" 
                unoptimized 
              />
              <div className="min-w-0">
                <h4 className="font-semibold text-[16px] text-white truncate">{activeUser.nickname}</h4>
                <button onClick={copyId} className="flex items-center gap-1.5 text-[13px] text-white/50 hover:text-[#667fff] transition-colors mt-0.5 cursor-pointer outline-none">
                  #{displayId} {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
            
            <div className="h-[1px] bg-white/10 w-full mb-4" />
            
            {/* ВНУТРЕННИЙ БЛОК БАЛАНСА */}
            {showBalance && showInnerBalance && (
              <button 
                onClick={() => { setIsDropdownOpen(false); handleOpenDeposit(); }} 
                className="w-full mb-4 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-transparent hover:border-white/10 transition-all group flex items-center justify-between outline-none cursor-pointer"
              >
                <div className="flex flex-col items-start">
                  <span className="text-[11px] uppercase font-semibold text-white/50 tracking-wider">{txtBalance}</span>
                  <span className="font-bold text-[20px] text-white mt-0.5">{formatCrystals(activeUser.crystals)} 💎</span>
                </div>
                <div className="w-10 h-10 rounded-full bg-[#667fff] flex items-center justify-center text-white shadow-lg shadow-[#667fff]/20 group-hover:scale-105 transition-transform">
                  <Plus size={18} />
                </div>
              </button>
            )}
            
            <div className="space-y-1.5 mb-3">
              {activeUser.hasAccess === false && (
                <a href="/buy/multiverse" target="_blank" rel="noopener noreferrer" className="w-full p-3 rounded-xl bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 transition-all flex items-center gap-3 text-[14px] font-medium cursor-pointer">
                  <ShoppingCart size={18} /> {txtBuy}
                </a>
              )} 

              {activeUser.roles === 'ADMIN' && (
                <Link href="https://admin.royolands.com/" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center gap-3 p-3 rounded-xl text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 transition-colors text-[14px] font-medium cursor-pointer">
                  <ShieldAlert size={18} /> {txtAdmin}
                </Link>
              )}

              <Link href="/me" onClick={() => setIsDropdownOpen(false)} className="w-full flex items-center gap-3 p-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors text-[14px] font-medium cursor-pointer">
                <LayoutDashboard size={18} /> {txtDash}
              </Link>
              <a href="https://account.royolands.com/settings" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 p-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors text-[14px] font-medium cursor-pointer">
                <Settings size={18} /> {txtSettings}
              </a>
            </div>
            
            <div className="h-[1px] bg-white/10 w-full mb-3" />
            
            <button onClick={() => { setIsDropdownOpen(false); activeLogout(); }} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-red-400/90 hover:text-red-400 hover:bg-red-400/10 transition-colors text-[14px] font-semibold cursor-pointer outline-none">
              <LogOut size={18} /> {txtLogout}
            </button>
          </div>
        </div>
      </div>
      
    </div>
  );
}